import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ProductFormMode = 'wizard' | 'single';
export type SupplyFormMode = 'wizard' | 'single';
export type ThemeMode = 'light' | 'dark';

interface PreferencesState {
  productFormMode: ProductFormMode;
  supplyFormMode: SupplyFormMode;
  sidebarCollapsed: boolean;
  theme: ThemeMode;
  setProductFormMode: (mode: ProductFormMode) => void;
  setSupplyFormMode: (mode: SupplyFormMode) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebarCollapsed: () => void;
  setTheme: (theme: ThemeMode) => void;
  applyTheme: () => void;
  syncFromServer: (prefs: Partial<Pick<PreferencesState, 'productFormMode' | 'supplyFormMode' | 'sidebarCollapsed' | 'theme'>>) => void;
}

const THEME_KEY = 'theme';
const SIDEBAR_KEY = 'sidebar_collapsed';
const FORM_MODE_KEY = 'product_form_mode';
const SUPPLY_FORM_MODE_KEY = 'supply_form_mode';

function applyThemeToDocument(theme: ThemeMode) {
  if (theme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
  localStorage.setItem(THEME_KEY, theme);
}

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set, get) => ({
      productFormMode: (localStorage.getItem(FORM_MODE_KEY) as ProductFormMode) || 'wizard',
      supplyFormMode: (localStorage.getItem(SUPPLY_FORM_MODE_KEY) as SupplyFormMode) || 'wizard',
      sidebarCollapsed: localStorage.getItem(SIDEBAR_KEY) === 'true',
      theme: (localStorage.getItem(THEME_KEY) as ThemeMode) || 'light',

      setProductFormMode: (mode) => {
        localStorage.setItem(FORM_MODE_KEY, mode);
        set({ productFormMode: mode });
      },

      setSupplyFormMode: (mode) => {
        localStorage.setItem(SUPPLY_FORM_MODE_KEY, mode);
        set({ supplyFormMode: mode });
      },

      setSidebarCollapsed: (collapsed) => {
        localStorage.setItem(SIDEBAR_KEY, String(collapsed));
        set({ sidebarCollapsed: collapsed });
      },

      toggleSidebarCollapsed: () => {
        const next = !get().sidebarCollapsed;
        localStorage.setItem(SIDEBAR_KEY, String(next));
        set({ sidebarCollapsed: next });
      },

      setTheme: (theme) => {
        applyThemeToDocument(theme);
        set({ theme });
      },

      applyTheme: () => {
        applyThemeToDocument(get().theme);
      },

      syncFromServer: (prefs) => {
        const updates: Partial<PreferencesState> = {};
        if (prefs.productFormMode) {
          updates.productFormMode = prefs.productFormMode;
          localStorage.setItem(FORM_MODE_KEY, prefs.productFormMode);
        }
        if (prefs.supplyFormMode) {
          updates.supplyFormMode = prefs.supplyFormMode;
          localStorage.setItem(SUPPLY_FORM_MODE_KEY, prefs.supplyFormMode);
        }
        if (prefs.sidebarCollapsed !== undefined) {
          updates.sidebarCollapsed = prefs.sidebarCollapsed;
          localStorage.setItem(SIDEBAR_KEY, String(prefs.sidebarCollapsed));
        }
        if (prefs.theme) {
          updates.theme = prefs.theme;
          applyThemeToDocument(prefs.theme);
        }
        set(updates);
      },
    }),
    {
      name: 'fun1st-preferences',
      partialize: (s) => ({
        productFormMode: s.productFormMode,
        supplyFormMode: s.supplyFormMode,
        sidebarCollapsed: s.sidebarCollapsed,
        theme: s.theme,
      }),
    }
  )
);
